import { useRef, useState } from "react";
import { applyCannedMergeTags, normalizeShortcut } from "@/lib/cannedReplyUtils";
import ComposerLinkControl from "@/components/chat/ComposerLinkControl";
import { CannedResponseBodyPreview } from "@/lib/CannedResponseBodyPreview";

export default function CannedResponsesPanel({
  user,
  activeTab,
  cannedResponses,
  cannedLoading,
  cannedError,
  onRetryLoad,
  onCreate,
  onInsert,
}) {
  const [expanded, setExpanded] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ shortcut: "", title: "", body: "", visibility: "public" });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const bodyTextareaRef = useRef(null);
  const previewRef = useRef(null);
  if (!previewRef.current) {
    previewRef.current = new CannedResponseBodyPreview();
  }
  const bodyPreview = previewRef.current;

  if (activeTab !== "reply") return null;

  const handleAdd = async (e) => {
    e.preventDefault();
    const shortcut = normalizeShortcut(form.shortcut);
    if (!shortcut) {
      setFormError("Add a shortcut (letters, numbers, hyphen only).");
      return;
    }
    if (!form.title.trim()) {
      setFormError("Add a title for this saved reply.");
      return;
    }
    if (!form.body.trim()) {
      setFormError("Add the message body.");
      return;
    }
    setFormError("");
    setSaving(true);
    try {
      await onCreate({
        shortcut: form.shortcut,
        title: form.title.trim(),
        body: form.body.trim(),
        visibility: form.visibility,
      });
      setForm({ shortcut: "", title: "", body: "", visibility: "public" });
      setShowAdd(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Could not save reply.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 rounded-2xl border border-slate-200/80 bg-white/90 shadow-sm ring-1 ring-slate-900/[0.03] dark:border-slate-700/70 dark:bg-slate-900/80 dark:ring-slate-700/20">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-2xl px-3 py-2.5 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50/80 sm:px-4 dark:text-slate-200 dark:hover:bg-slate-800/50"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-2">
          <i className="ri-bookmark-3-line text-base text-teal-600" aria-hidden />
          Saved replies
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            {cannedResponses.length}
          </span>
        </span>
        <i className={`ri-arrow-${expanded ? "up" : "down"}-s-line text-lg text-slate-400 dark:text-slate-500`} aria-hidden />
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-3 pb-3 pt-1 sm:px-4 dark:border-slate-700/60">
          {cannedLoading && (
            <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">Loading saved replies…</p>
          )}
          {cannedError && (
            <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50/80 px-2 py-1.5 text-xs text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-300">
              <span>{cannedError.message || "Could not load saved replies."}</span>
              {onRetryLoad && (
                <button type="button" onClick={onRetryLoad} className="font-semibold text-rose-900 underline dark:text-rose-200">
                  Retry
                </button>
              )}
            </div>
          )}
          <p className="mb-2 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
            Type <kbd className="rounded border border-slate-200 bg-slate-50 px-1 font-mono text-[10px] dark:border-slate-600 dark:bg-slate-800">/</kbd> then a shortcut in the
            composer (Intercom-style). Merge tags:{" "}
            <code className="text-[10px] text-teal-700 dark:text-teal-400">{"{{first_name}}"}</code>,{" "}
            <code className="text-[10px] text-teal-700 dark:text-teal-400">{"{{full_name}}"}</code>,{" "}
            <code className="text-[10px] text-teal-700 dark:text-teal-400">{"{{company_name}}"}</code>,{" "}
            <code className="text-[10px] text-teal-700 dark:text-teal-400">{"{{email}}"}</code>
          </p>

          <ul className="max-h-36 space-y-1 overflow-y-auto pr-0.5">
            {cannedResponses.map((cr) => (
              <li key={cr.id}>
                <button
                  type="button"
                  onClick={() => onInsert(applyCannedMergeTags(cr.body, user))}
                  className="flex w-full items-start gap-2 rounded-xl px-2 py-2 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/60"
                >
                  <span className="shrink-0 rounded-md bg-slate-900 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-white dark:bg-slate-700">
                    /{cr.shortcut}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-800 dark:text-slate-200">{cr.title}</span>
                    {bodyPreview.hasContent(cr.body) ? (
                      <span
                        className="line-clamp-2 text-[11px] text-slate-500 dark:text-slate-400 [&_a]:text-teal-700 [&_a]:underline"
                        dangerouslySetInnerHTML={{ __html: bodyPreview.toHtml(cr.body) }}
                      />
                    ) : null}
                  </span>
                  <i className="ri-text-snippet shrink-0 text-slate-300 dark:text-slate-600" aria-hidden title="Insert" />
                </button>
              </li>
            ))}
          </ul>

          {!showAdd ? (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-200 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-teal-200 hover:bg-teal-50/40 hover:text-teal-800 dark:border-slate-700 dark:text-slate-400 dark:hover:border-teal-700 dark:hover:bg-teal-950/30 dark:hover:text-teal-300"
            >
              <i className="ri-add-line text-base" aria-hidden />
              Add saved reply
            </button>
          ) : (
            <form onSubmit={handleAdd} className="mt-3 space-y-2 rounded-xl border border-slate-200/80 bg-slate-50/50 p-3 dark:border-slate-700/60 dark:bg-slate-800/40">
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Shortcut</span>
                  <div className="flex items-center rounded-lg border border-slate-200 bg-white px-2 ring-1 ring-transparent focus-within:border-teal-300 focus-within:ring-teal-500/15 dark:border-slate-600 dark:bg-slate-800">
                    <span className="text-sm text-slate-400 dark:text-slate-500">/</span>
                    <input
                      value={form.shortcut}
                      onChange={(e) => setForm((f) => ({ ...f, shortcut: e.target.value }))}
                      className="w-full border-0 bg-transparent py-1.5 pl-0.5 text-sm outline-none dark:text-slate-200 dark:placeholder:text-slate-500"
                      placeholder="e.g. refund"
                      autoComplete="off"
                    />
                  </div>
                </label>
                <label className="block sm:col-span-1">
                  <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Title</span>
                  <input
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none ring-1 ring-transparent focus:border-teal-300 focus:ring-teal-500/15 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500"
                    placeholder="Shown in picker"
                  />
                </label>
              </div>
              <label className="block">
                <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Message</span>
                <textarea
                  ref={bodyTextareaRef}
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  rows={3}
                  className="w-full resize-none rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none ring-1 ring-transparent focus:border-teal-300 focus:ring-teal-500/15 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500"
                  placeholder={"Hi {{first_name}},\n\nInclude links as [label](https://example.com)"}
                />
                <div className="mt-1.5 flex items-center gap-2">
                  <ComposerLinkControl
                    disabled={saving}
                    textareaRef={bodyTextareaRef}
                    value={form.body}
                    onChange={(next) => setForm((f) => ({ ...f, body: next }))}
                  />
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">Add a hyperlink to the saved reply</span>
                </div>
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Visibility</span>
                <label className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="radio"
                    name="visibility"
                    value="public"
                    checked={form.visibility === "public"}
                    onChange={() => setForm((f) => ({ ...f, visibility: "public" }))}
                    className="accent-teal-600"
                  />
                  <span className="text-xs text-slate-700 dark:text-slate-300">Public — all agents</span>
                </label>
                <label className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="radio"
                    name="visibility"
                    value="pvt"
                    checked={form.visibility === "pvt"}
                    onChange={() => setForm((f) => ({ ...f, visibility: "pvt" }))}
                    className="accent-teal-600"
                  />
                  <span className="text-xs text-slate-700 dark:text-slate-300">Private — only me</span>
                </label>
                <label className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="radio"
                    name="visibility"
                    value="workspace_wide"
                    checked={form.visibility === "workspace_wide"}
                    onChange={() => setForm((f) => ({ ...f, visibility: "workspace_wide" }))}
                    className="accent-teal-600"
                  />
                  <span className="text-xs text-slate-700 dark:text-slate-300">
                    Workspace-wide — all my workspaces
                  </span>
                </label>
              </div>
              {form.visibility === "workspace_wide" && (
                <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                  Available to agents in every workspace where you are enrolled. Access is removed from a workspace if you leave it.
                </p>
              )}
              {formError && <p className="text-xs font-medium text-rose-600 dark:text-rose-400">{formError}</p>}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAdd(false);
                    setFormError("");
                  }}
                  className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200/60 dark:text-slate-300 dark:hover:bg-slate-700/60"
                >
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="ui-btn-primary !py-1.5 !text-xs disabled:opacity-50">
                  {saving ? "Saving…" : "Save reply"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
